/**
 * v4.8.0 — Reusable operational intelligence components.
 *
 *   <SeverityChip />          — 4-tier chip; theme-safe; reused everywhere.
 *   <RecoveryConfidenceChip /> — Stable/recovery_likely/at_risk/escalation_likely.
 *   <InsightStrip />          — Compact "Why this matters" bullet strip.
 *   <NextActionRow />         — Advisory next-step row (max 2 actions).
 *   <TimingFootprint />       — Calm timing indicators (received/age/response).
 *   <GroupHeader />           — Triage group header (icon + label + count).
 *
 * Discipline:
 *   * No alarmist colors. Critical is muted crimson, never bright red.
 *   * No punitive framing — timing is awareness, not surveillance.
 *   * All copy passes through useTranslation (i18n keys come from API).
 */
import React from "react";
import { useTranslation } from "react-i18next";
import {
    AlertOctagon, LifeBuoy, Activity, CheckCircle2,
    Clock, ArrowRight,
} from "lucide-react";
import { SEVERITY_TOKENS, RECOVERY_CONFIDENCE_TOKENS, formatAge } from "../../lib/operational-intelligence/tokens";

const ICONS = {
    "alert-octagon": AlertOctagon,
    "life-buoy": LifeBuoy,
    "activity": Activity,
    "check-circle-2": CheckCircle2,
};

/* ─────────── Severity chip ─────────── */
export const SeverityChip = ({ severity, size = "sm", testId }) => {
    const { t } = useTranslation();
    const tok = SEVERITY_TOKENS[severity] || SEVERITY_TOKENS.low;
    const fontSize = size === "lg" ? "0.85rem" : "0.72rem";
    return (
        <span
            data-testid={testId || `intel-severity-${severity}`}
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 uppercase tracking-wider font-medium"
            style={{
                color: tok.text,
                background: tok.bg,
                border: `1px solid ${tok.border}`,
                fontSize,
                letterSpacing: "0.08em",
            }}
        >
            <span
                className="inline-block rounded-full"
                style={{
                    width: 6, height: 6,
                    background: tok.text,
                    boxShadow: `0 0 6px ${tok.glow}`,
                }}
            />
            {t(`intel.severity.${severity}`)}
        </span>
    );
};

/* ─────────── Recovery confidence chip ─────────── */
export const RecoveryConfidenceChip = ({ confidence, testId }) => {
    const { t } = useTranslation();
    const tok = RECOVERY_CONFIDENCE_TOKENS[confidence] || RECOVERY_CONFIDENCE_TOKENS.stable;
    return (
        <span
            data-testid={testId || `intel-confidence-${confidence}`}
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px]"
            style={{
                color: tok.text,
                background: tok.bg,
                fontFamily: "Outfit, sans-serif",
            }}
        >
            {t(`intel.confidence.${confidence}`)}
        </span>
    );
};

/* ─────────── Insight strip ("Why this matters") ─────────── */
export const InsightStrip = ({ reasons = [], testId }) => {
    const { t } = useTranslation();
    if (!reasons.length) return null;
    return (
        <div
            data-testid={testId || "intel-insight-strip"}
            data-insight-id="review-shield.insight-strip"
            data-insight-label="Why this matters"
            className="rounded-lg px-3 py-2 mt-3"
            style={{
                background: "rgba(255,255,255,0.025)",
                border: "1px solid rgba(255,255,255,0.06)",
            }}
        >
            <p
                className="ob-eyebrow !mb-1.5"
                style={{ fontSize: "0.62rem" }}
            >
                {t("intel.why_this_matters")}
            </p>
            <ul className="space-y-0.5">
                {reasons.slice(0, 3).map((rk) => (
                    <li
                        key={rk}
                        className="text-xs leading-relaxed"
                        style={{ color: "var(--ob-text-secondary)" }}
                    >
                        · {t(`intel.reason.${rk}`)}
                    </li>
                ))}
            </ul>
        </div>
    );
};

/* ─────────── Suggested next action row ─────────── */
export const NextActionRow = ({ actions = [], testId }) => {
    const { t } = useTranslation();
    if (!actions.length) return null;
    return (
        <div
            data-testid={testId || "intel-next-action"}
            data-insight-id="review-shield.next-actions"
            data-insight-label="Suggested next actions"
            className="flex items-center gap-2 flex-wrap mt-3"
        >
            <span
                className="text-[10px] uppercase tracking-wider"
                style={{ color: "var(--ob-text-tertiary)" }}
            >
                {t("intel.suggested_action")}
            </span>
            {actions.slice(0, 2).map((ak) => (
                <span
                    key={ak}
                    className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1"
                    style={{
                        color: "#A7F3D0",
                        background: "rgba(16, 185, 129, 0.08)",
                        border: "1px solid rgba(16, 185, 129, 0.22)",
                        fontFamily: "Outfit, sans-serif",
                    }}
                >
                    <ArrowRight size={11} strokeWidth={1.75} />
                    {t(`intel.action.${ak}`)}
                </span>
            ))}
        </div>
    );
};

/* ─────────── Timing footprint ─────────── */
export const TimingFootprint = ({ ageHours, hasResponse, testId }) => {
    const { t } = useTranslation();
    const ageColor = ageHours >= 24
        ? "#FB923C"
        : ageHours >= 4 ? "#FCD34D" : "var(--ob-text-tertiary)";
    return (
        <span
            data-testid={testId || "intel-timing"}
            className="inline-flex items-center gap-1 text-[11px]"
            style={{ color: ageColor, fontFamily: "Outfit, sans-serif" }}
        >
            <Clock size={11} strokeWidth={1.75} />
            {formatAge(ageHours)}
            {!hasResponse && (
                <span className="ml-1 italic" style={{ color: "var(--ob-text-tertiary)" }}>
                    · {t("intel.no_response_yet")}
                </span>
            )}
        </span>
    );
};

/* ─────────── Group header ─────────── */
export const GroupHeader = ({ groupKey, labelKey, icon, count, tone, testId }) => {
    const { t } = useTranslation();
    const Icon = ICONS[icon] || Activity;
    const accent = tone === "supportive" ? "#FB923C" : "#94A3B8";
    return (
        <div
            data-testid={testId || `intel-group-${groupKey}`}
            className="flex items-center justify-between mt-8 mb-4"
        >
            <div className="flex items-center gap-2.5">
                <Icon size={16} strokeWidth={1.75} style={{ color: accent }} />
                <h2
                    className="text-base"
                    style={{
                        color: "var(--ob-text-primary)",
                        fontFamily: "Outfit, sans-serif",
                        fontWeight: 500,
                    }}
                >
                    {t(labelKey)}
                </h2>
            </div>
            <span
                className="text-xs px-2.5 py-0.5 rounded-full"
                style={{
                    color: "var(--ob-text-tertiary)",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.07)",
                }}
            >
                {count}
            </span>
        </div>
    );
};
